/**
 * Calculate center value for profiles with >=3 segments
 *
 * This utility function calculates the total length of a profile including cut overhead.
 * Only profiles with 4+ points (3+ segments) should be considered.
 *
 * @param pointCount - Number of points in the profile
 * @param segmentLengths - Array of segment lengths in mm
 * @returns The total length in mm including cut overhead, or null if profile has <3 segments
 */
export function calculateProfileCenterValue(
  pointCount: number,
  segmentLengths: number[],
): number | null {
  // Nur Profile mit 3+ Segmenten (4+ Punkten) berücksichtigen
  // Anzahl Segmente = Anzahl Punkte - 1
  // 3 Segmente = 4 Punkte
  if (pointCount < 4) {
    return null;
  }

  const segmentCount = pointCount - 1; // Anzahl Segmente
  const cutCount = segmentCount - 1; // Anzahl Schnitte (zwischen den Segmenten)

  // Summe der Segmentlängen
  const totalSegmentLength = segmentLengths.reduce((sum, len) => sum + len, 0);

  // Gesamtlänge mit Schnitt-Overhead (2mm pro Schnitt)
  return Math.floor(totalSegmentLength) + cutCount * 2;
}

/**
 * Calculate center value for multiple profiles
 *
 * Filters profiles with >=3 segments, calculates their lengths, and returns
 * the common length if all filtered profiles have the same length.
 *
 * @param profiles - Array of objects with pointCount and segmentLengths
 * @returns The common length in mm if all profiles have the same length, null otherwise
 */
export function calculateCommonProfileCenterValue(
  profiles: Array<{ pointCount: number; segmentLengths: number[] }>,
): number | null {
  // Filter profiles with >=3 segments
  const filteredProfiles = profiles.filter((p) => p.pointCount >= 4);

  if (filteredProfiles.length === 0) {
    return null;
  }

  // Calculate length for each filtered profile
  const lengths = filteredProfiles
    .map((p) => calculateProfileCenterValue(p.pointCount, p.segmentLengths))
    .filter((len): len is number => len !== null);

  if (lengths.length === 0) {
    return null;
  }

  // Check if all profiles have the same length
  const uniqueLengths = new Set(lengths);
  if (uniqueLengths.size === 1) {
    return lengths[0] ?? null;
  }

  return null;
}
